import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiGet } from '../utils/api';
import FbrTokenBanner from './FbrTokenBanner';
import {
  Home,
  Package,
  Users,
  FileText,
  Plus,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  BarChart3,
  DollarSign,
  TrendingUp,
  FolderIcon,
  TestTube,
  RefreshCw,
  Zap,
  Crown
} from './Icons';

const Navigation = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tenant, setTenant] = useState(null);
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.tenant_id) return;
    apiGet('/api/tenant/profile').then((response) => {
      if (response.ok) setTenant(response.data.tenant || response.data);
    }).catch(() => {});
  }, [user?.tenant_id]);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Products', href: '/products', icon: Package },
    { name: 'Categories', href: '/categories', icon: FolderIcon },
    { name: 'Clients', href: '/clients', icon: Users },
    { name: 'Invoices', href: '/invoices', icon: FileText },
    { name: 'Create Invoice', href: '/create-invoice', icon: Plus },
    { name: 'Batch Management', href: '/batch-management', icon: RefreshCw },
    { name: 'Direct FBR Submission', href: '/direct-fbr-submission', icon: Zap },
    { name: 'Settings', href: '/settings', icon: Settings },
    { name: 'Pro Features', href: '/pro', icon: Crown, badge: 'Pro' },
  ];

  const adminNavigation = [
    { name: 'Admin Dashboard', href: '/admin', icon: Shield },
    { name: 'Tenants', href: '/admin/tenants', icon: BarChart3 },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Payments', href: '/admin/payments', icon: DollarSign },
    { name: 'Site Analytics', href: '/admin/site-analytics', icon: TrendingUp },
  ];

  const handleLogout = () => {
    logout();
  };

  const isCurrentPath = (path) => {
    // Pro Features has one sidebar entry but a URL per tab.
    if (path === '/pro') return location.pathname === '/pro' || location.pathname.startsWith('/pro/');
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-40 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)}></div>
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-slate-900">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent
            navigation={navigation}
            adminNavigation={adminNavigation}
            isAdmin={isAdmin}
            user={user}
            tenant={tenant}
            isCurrentPath={isCurrentPath}
            handleLogout={handleLogout}
          />
        </div>
      </div>

      {/* Desktop sidebar - Fixed and Sticky */}
      <div className="hidden lg:flex lg:flex-shrink-0 lg:fixed lg:inset-y-0 lg:z-50">
        <div className="flex flex-col w-64">
          <SidebarContent
            navigation={navigation}
            adminNavigation={adminNavigation}
            isAdmin={isAdmin}
            user={user}
            tenant={tenant}
            isCurrentPath={isCurrentPath}
            handleLogout={handleLogout}
          />
        </div>
      </div>

      {/* Main content - With left margin for fixed sidebar */}
      <div className="lg:ml-64 flex-1 overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden">
          <div className="flex items-center justify-between bg-white px-4 py-3 border-b border-slate-200 shadow-sm">
            <button
              type="button"
              className="p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-semibold text-slate-900">POS FBR</h1>
            <div className="w-8"></div>
          </div>
        </div>

        {/* FBR Token Warning Banner */}
        <FbrTokenBanner />

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          {children}
        </main>

        {/* Scroll to top button */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-4 right-4 lg:bottom-8 lg:right-8 bg-slate-800 text-white p-3 rounded-full shadow-lg shadow-slate-800/30 hover:bg-slate-900 hover:scale-105 transition-all duration-300 z-40"
          title="Scroll to top"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const SidebarContent = ({ navigation, adminNavigation, isAdmin, user, tenant, isCurrentPath, handleLogout }) => (
  <div className="flex flex-col h-screen bg-slate-900">
    {/* Logo */}
    <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
      <div className="flex items-center flex-shrink-0 px-4 pb-4">
        <div className="flex items-center justify-center w-full bg-white/95 rounded-xl py-2">
          <img
            src="/marketing/images/axiom_square.jpeg"
            alt="AXIOM SQUARE"
            className="h-14 w-auto object-contain"
          />
        </div>
      </div>

      {/* Tenant logo + name - wraps instead of truncating for long business names */}
      {tenant && (
        <div className="mx-4 mb-2 flex items-start gap-2 rounded-lg bg-slate-800/60 px-3 py-2.5">
          {tenant.logo_path ? (
            <img
              src={`/api/tenant/logo-file/${tenant.id}`}
              alt={tenant.name}
              className="h-8 w-8 shrink-0 rounded bg-white object-contain p-0.5"
            />
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-slate-700 text-xs font-semibold text-slate-300">
              {tenant.name?.[0]?.toUpperCase() || 'B'}
            </div>
          )}
          <div className="min-w-0 break-words text-sm font-medium leading-snug text-white">{tenant.name}</div>
        </div>
      )}

      {/* Separator line */}
      <div className="border-b border-slate-700/60 mx-4"></div>

      {/* User info - just email + role. The business name above already
          carries the "name" for this sidebar, so we don't repeat a second
          name-like line here. */}
      <div className="px-4 mt-6">
        <div className="flex items-center">
          <div className="bg-slate-700 rounded-full p-2">
            <Users className="h-5 w-5 text-slate-300" />
          </div>
          <div className="ml-3 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.email}</p>
            <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500/20 text-amber-400 mt-1">
              {user?.role === 'admin' ? 'System Admin' : 'Tenant Admin'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="mt-8 flex-1 px-3 space-y-1">
        <div className="mb-2">
          <h3 className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Main Menu
          </h3>
        </div>
        {navigation.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`${
                isCurrentPath(item.href)
                  ? 'bg-amber-600 text-white shadow-sm'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              } group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200`}
            >
              <Icon
                className={`${
                  isCurrentPath(item.href) ? 'text-white' : 'text-slate-400 group-hover:text-white'
                } h-5 w-5 shrink-0`}
              />
              <span className="flex-1">{item.name}</span>
              {item.badge && (
                <span className="rounded-full bg-amber-400/90 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-900">
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}

        {/* Admin Navigation */}
        {isAdmin && (
          <>
            <div className="mt-8 mb-2">
              <h3 className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Administration
              </h3>
            </div>
            {adminNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    isCurrentPath(item.href)
                      ? 'bg-red-600 text-white shadow-sm'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  } group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200`}
                >
                  <Icon
                    className={`${
                      isCurrentPath(item.href) ? 'text-white' : 'text-slate-400 group-hover:text-white'
                    } h-5 w-5 shrink-0`}
                  />
                  {item.name}
                </Link>
              );
            })}
          </>
        )}
      </nav>
    </div>

    {/* User actions - Always visible at bottom */}
    <div className="flex-shrink-0 flex border-t border-slate-700/60 p-4">
      <button
        onClick={handleLogout}
        className="flex-shrink-0 w-full group flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-slate-300 hover:bg-slate-800 hover:text-white transition-colors duration-200"
      >
        <LogOut className="h-5 w-5 shrink-0 text-slate-400 group-hover:text-white" />
        <p className="text-sm font-medium">
          Sign out
        </p>
      </button>
    </div>
  </div>
);

export default Navigation;
